import {
  AD_MANAGER_ASPECT_RATIO_TOLERANCE,
  AD_MANAGER_MAX_IMAGE_SIZE_BYTES,
  AD_MANAGER_MAX_VIDEO_SIZE_BYTES,
  AD_MANAGER_MEDIA_PLACEMENT_RATIO_RANGES,
  AD_MANAGER_MIN_SHORTEST_SIDE_PX,
  AD_MANAGER_RECOMMENDED_SHORTEST_SIDE_PX
} from '@/config/constants';

export type MediaPlacementCategory = 'feed' | 'stories';

export type MediaValidationKind = 'image' | 'video';

export interface MediaValidationLimits {
  /** Max size in bytes for images. Default 30 MB. */
  maxImageSizeBytes?: number;
  /** Max size in bytes for videos. Default 1 GB. */
  maxVideoSizeBytes?: number;
  /** Hard reject when the shorter side is below this many pixels. Default 600 (Meta minimum). */
  minShortestSidePx?: number;
  /** Warning threshold on the shorter side. Default 1080 (Meta recommendation). */
  recommendedShortestSidePx?: number;
  /** Tolerance for aspect-ratio comparisons so rounding doesn't reject e.g. 1080×1350. Default 0.01. */
  aspectRatioTolerance?: number;
}

export interface MediaValidationInput {
  size: number;
  width?: number;
  height?: number;
  kind: MediaValidationKind;
}

export type MediaValidationIssueCode =
  | 'imageSizeExceeded'
  | 'videoSizeExceeded'
  | 'resolutionTooLow'
  | 'resolutionBelowRecommended'
  | 'aspectRatioOutOfRange';

export interface MediaValidationIssue {
  code: MediaValidationIssueCode;
  detail?: Record<string, number>;
}

export interface MediaValidationResult {
  errors: MediaValidationIssue[];
  warnings: MediaValidationIssue[];
}

const DEFAULT_LIMITS: Required<MediaValidationLimits> = {
  maxImageSizeBytes: AD_MANAGER_MAX_IMAGE_SIZE_BYTES,
  maxVideoSizeBytes: AD_MANAGER_MAX_VIDEO_SIZE_BYTES,
  minShortestSidePx: AD_MANAGER_MIN_SHORTEST_SIDE_PX,
  recommendedShortestSidePx: AD_MANAGER_RECOMMENDED_SHORTEST_SIDE_PX,
  aspectRatioTolerance: AD_MANAGER_ASPECT_RATIO_TOLERANCE
};

/**
 * Intersect the ratio ranges of the given placement categories. When an asset
 * is used across multiple placements the tighter (feed) range wins because
 * the same asset has to survive every target.
 */
export const getRatioBoundsForPlacements = (
  categories: MediaPlacementCategory[]
): { min: number; max: number } | null => {
  if (categories.length === 0) return null;
  return categories.reduce<{ min: number; max: number } | null>(
    (bounds, category) => {
      const next = AD_MANAGER_MEDIA_PLACEMENT_RATIO_RANGES[category];
      if (!bounds) return { ...next };
      return {
        min: Math.max(bounds.min, next.min),
        max: Math.min(bounds.max, next.max)
      };
    },
    null
  );
};

/**
 * Map a Meta ad placement id like `facebook-feed` / `instagram-stories` to
 * the ratio category. Returns `null` for unknown suffixes.
 */
export const getPlacementCategoryFromKey = (
  key: string
): MediaPlacementCategory | null => {
  if (key.endsWith('-feed')) return 'feed';
  if (key.endsWith('-stories') || key.endsWith('-reels')) return 'stories';
  return null;
};

export const getPlacementCategoriesFromKeys = (
  keys: string[]
): MediaPlacementCategory[] =>
  Array.from(
    new Set(
      keys
        .map(getPlacementCategoryFromKey)
        .filter((category): category is MediaPlacementCategory =>
          category !== null
        )
    )
  );

/**
 * Pure validation of a single media asset against Meta's advertising rules.
 * Callers translate the returned codes for their UI.
 */
export const validateMediaConstraints = (
  media: MediaValidationInput,
  placementCategories: MediaPlacementCategory[] = [],
  limits: MediaValidationLimits = {}
): MediaValidationResult => {
  const opts = { ...DEFAULT_LIMITS, ...limits };
  const errors: MediaValidationIssue[] = [];
  const warnings: MediaValidationIssue[] = [];

  if (media.kind === 'image' && media.size > opts.maxImageSizeBytes) {
    errors.push({
      code: 'imageSizeExceeded',
      detail: { size: media.size, limit: opts.maxImageSizeBytes }
    });
  }
  if (media.kind === 'video' && media.size > opts.maxVideoSizeBytes) {
    errors.push({
      code: 'videoSizeExceeded',
      detail: { size: media.size, limit: opts.maxVideoSizeBytes }
    });
  }

  if (media.width && media.height) {
    const shortestSide = Math.min(media.width, media.height);
    if (shortestSide < opts.minShortestSidePx) {
      errors.push({
        code: 'resolutionTooLow',
        detail: { shortestSide, minimum: opts.minShortestSidePx }
      });
    } else if (shortestSide < opts.recommendedShortestSidePx) {
      warnings.push({
        code: 'resolutionBelowRecommended',
        detail: {
          shortestSide,
          recommended: opts.recommendedShortestSidePx
        }
      });
    }

    const bounds = getRatioBoundsForPlacements(placementCategories);
    if (bounds && media.width > 0 && media.height > 0) {
      const ratio = media.width / media.height;
      const tolerance = opts.aspectRatioTolerance;
      if (ratio < bounds.min - tolerance || ratio > bounds.max + tolerance) {
        errors.push({
          code: 'aspectRatioOutOfRange',
          detail: {
            ratio: Number(ratio.toFixed(3)),
            min: bounds.min,
            max: bounds.max
          }
        });
      }
    }
  }

  return { errors, warnings };
};
